import { create } from 'zustand'

interface KGNode {
  id: string
  label: string
  type: string
  properties: Record<string, any>
  isCenter?: boolean
}

interface KGLink {
  source: string
  target: string
  type: string
  properties: Record<string, any>
}

interface KGSubgraph {
  nodes: KGNode[]
  links: KGLink[]
  total_nodes: number
  total_links: number
}

interface KGOverview {
  node_count: number
  relation_count: number
  node_types: Array<{ type: string[]; count: number }>
  relation_types: Array<{ type: string; count: number }>
}

interface KGStore {
  overview: KGOverview | null
  subgraph: KGSubgraph | null
  loading: boolean
  error: string | null
  
  // Actions
  fetchOverview: () => Promise<void>
  searchSubgraph: (searchType: string, searchInput: string) => Promise<void>
  expandNode: (nodeId: string, nodeType: string, nodeLabel: string) => Promise<void>
  clearSubgraph: () => void
  setError: (error: string | null) => void
}

// 使用相对路径，通过 nginx 代理到后端
const API_BASE_URL = '/api'

export const useKGStore = create<KGStore>((set, get) => ({
  overview: null,
  subgraph: null,
  loading: false,
  error: null,

  fetchOverview: async () => {
    set({ loading: true, error: null })
    try {
      const response = await fetch(`${API_BASE_URL}/kg/overview`)
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      const result = await response.json()
      if (result.success) {
        set({ overview: result.data, loading: false })
      } else {
        throw new Error(result.message || '获取图谱概览失败')
      }
    } catch (error) {
      console.error('获取图谱概览失败:', error)
      set({ 
        error: error instanceof Error ? error.message : '获取图谱概览失败',
        loading: false 
      })
    }
  },

  searchSubgraph: async (searchType: string, searchInput: string) => {
    set({ loading: true, error: null })
    try {
      const response = await fetch(`${API_BASE_URL}/kg/search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          search_type: searchType,
          search_input: searchInput
        })
      })
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      const result = await response.json()
      if (result.success) {
        set({ subgraph: result.data, loading: false })
      } else {
        throw new Error(result.message || '搜索子图失败')
      }
    } catch (error) {
      console.error('搜索子图失败:', error)
      set({ 
        error: error instanceof Error ? error.message : '搜索子图失败',
        loading: false 
      })
    }
  },

  expandNode: async (nodeId: string, nodeType: string, nodeLabel: string) => {
    set({ loading: true, error: null })
    try {
      const response = await fetch(`${API_BASE_URL}/kg/expand-node`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          node_id: nodeId,
          node_type: nodeType,
          node_label: nodeLabel
        })
      })
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      const result = await response.json()
      if (result.success) {
        set({ subgraph: result.data, loading: false })
      } else {
        throw new Error(result.message || '节点扩展失败')
      }
    } catch (error) {
      console.error('节点扩展失败:', error)
      set({ 
        error: error instanceof Error ? error.message : '节点扩展失败',
        loading: false 
      })
    }
  },

  clearSubgraph: () => {
    set({ subgraph: null })
  },

  setError: (error: string | null) => {
    set({ error })
  }
}))